import { defineSemanticTokens } from '@chakra-ui/react'

/**
 * Roles. This is the layer components are allowed to touch.
 *
 * Values are light-only and carry no `_dark` condition, which is deliberate.
 * The colour-mode machinery stays wired and dormant; nothing here guesses at a
 * dark palette nobody has designed. What keeps that retrofit cheap is not
 * pre-declared dark values but the rule that no component references a raw
 * scale value — hold that and dark mode is an edit to this file alone.
 *
 * Note on `bg` and `fg`: Chakra's defaults resolve them to `{colors.white}`
 * and `{colors.black}`, NOT through `gray`. Overriding the `gray` scale alone
 * leaves the canvas pure white and the ink pure black, which is most of what
 * this design is not. They have to be named explicitly, here.
 */
export const semanticTokens = defineSemanticTokens({
  colors: {
    bg: {
      // the canvas
      DEFAULT: { value: '{colors.gray.100}' },
      subtle: { value: '{colors.gray.50}' },
      muted: { value: '{colors.gray.200}' },
      emphasized: { value: '{colors.gray.300}' },
      /** The floating detail panel and the composer sit on this. */
      panel: { value: '{colors.gray.50}' },
    },

    fg: {
      /** Ink is green, not black. Pointing this at gray.900 reads as a different product. */
      DEFAULT: { value: '{colors.forest.900}' },
      muted: { value: '{colors.forest.600}' },
      subtle: { value: '{colors.forest.500}' },
    },

    /**
     * The sidebar is the one surface that inverts — dark green ground, pale
     * ink — and none of Chakra's own roles describe it. It gets named roles
     * here rather than letting the component reach for `forest.900` directly,
     * which is the rule this whole layer exists to keep.
     */
    sidebar: {
      bg: { value: '{colors.forest.900}' },
      fg: { value: '{colors.forest.100}' },
      /** Hover and the current page share a value; the reference draws them alike. */
      hover: { value: '{colors.forest.800}' },
      active: { value: '{colors.forest.800}' },
      /**
       * A filled control on the dark ground. It inverts — pale fill, dark ink —
       * because a forest-solid button on a forest-900 sidebar would disappear.
       */
      solid: { value: '{colors.forest.100}' },
      solidHover: { value: '{colors.forest.200}' },
    },

    border: {
      DEFAULT: { value: '{colors.gray.300}' },
      muted: { value: '{colors.gray.200}' },
      subtle: { value: '{colors.gray.200}' },
      emphasized: { value: '{colors.gray.400}' },
    },

    /**
     * The default palette, pinned to its light values.
     *
     * A control with no `colorPalette` reads these keys, and Chakra's defaults
     * give each a `_dark` value. This file carries none, so in dark mode the
     * canvas stayed cream while `gray.fg` became `gray.200` — a button's label
     * drawn in the colour of the page it sits on. Every key a recipe reads is
     * named here so none falls through to the upstream pair.
     */
    gray: {
      contrast: { value: '{colors.white}' },
      fg: { value: '{colors.gray.800}' },
      subtle: { value: '{colors.gray.100}' },
      muted: { value: '{colors.gray.200}' },
      emphasized: { value: '{colors.gray.300}' },
      solid: { value: '{colors.gray.900}' },
      focusRing: { value: '{colors.gray.400}' },
      border: { value: '{colors.gray.200}' },
    },

    /**
     * What makes `colorPalette="forest"` work.
     *
     * Chakra's recipes read exactly these seven keys. Defining the `forest`
     * scale without them is not an error and reports nothing — the component
     * simply renders unstyled, which is the more expensive failure. If a
     * `colorPalette="forest"` element ever looks default, a missing key here is
     * the first place to look.
     */
    forest: {
      solid: { value: '{colors.forest.900}' },
      contrast: { value: '{colors.gray.50}' },
      fg: { value: '{colors.forest.800}' },
      muted: { value: '{colors.forest.100}' },
      subtle: { value: '{colors.forest.50}' },
      emphasized: { value: '{colors.forest.200}' },
      focusRing: { value: '{colors.forest.600}' },
    },

    /**
     * The accent, wired the same way so `colorPalette="rust"` is available to
     * the one thing that earns it.
     */
    rust: {
      solid: { value: '{colors.rust.600}' },
      contrast: { value: '{colors.rust.50}' },
      fg: { value: '{colors.rust.700}' },
      muted: { value: '{colors.rust.100}' },
      subtle: { value: '{colors.rust.50}' },
      emphasized: { value: '{colors.rust.200}' },
      focusRing: { value: '{colors.rust.500}' },
    },
  },

  /**
   * Chakra's component recipes reference these three rather than the raw radii
   * scale, so setting them moves the roundness of the whole library at once.
   *
   * These are semantic tokens upstream (`{radii.xs|sm|md}` by default), which
   * is why they live here and not in `./tokens`.
   */
  radii: {
    // badges, small controls
    l1: { value: '6px' },
    // nav items, inputs
    l2: { value: '10px' },
    // cards, the composer, the detail panel
    l3: { value: '14px' },
  },
})
